/**
 * screens/Settings/components/OptionListsCard
 * ──────────────────────────────────────────────────────────────────────────
 * The five option lists, each a row: label, note, Add button, then the values
 * as removable chips.
 *
 * REMOVING A VALUE DOES NOT DELETE IT. It leaves the picker; every record that
 * already carries it keeps it, and the footer says so. That is invariant 6, and
 * it is the difference between "we stopped offering this" and "the history now
 * refers to something that does not exist".
 *
 * SCOPE IS NOT HERE. Open / purpose / collection is a fixed enum because each
 * value changes behaviour — a purpose wallet expires, a collection reconciles
 * against what Billing says is owed. A configurable scope would be a value the
 * engine has no rules for. The footer states that too.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { StyleSheet, Text, View } from 'react-native';
import { Button, RemovableChip } from '../../../components';
import { neutral } from '../../../theme/colors';
import { sans, size as fontSize, leading } from '../../../theme/typography';
import {
  LIST_KINDS,
  LIST_META,
  type ListKind,
  type OptionRow,
} from '../../../schemas/config';
import SettingsCard from './SettingsCard';
import SettingsRow from './SettingsRow';

export interface OptionListsCardProps {
  optionsByKind: Record<ListKind, OptionRow[]>;
  onAdd: (kind: ListKind) => void;
  onRemove: (option: OptionRow) => void;
}

export function OptionListsCard({ optionsByKind, onAdd, onRemove }: OptionListsCardProps) {
  return (
    <SettingsCard
      title="Option lists"
      subtitle="What appears in the pickers when you assign cash. Removing a value keeps it on existing records."
      footNote="Scope — open, purpose, collection — is fixed. Each one changes how the assignment behaves, not just its label."
    >
      {LIST_KINDS.map((kind, index) => {
        const meta = LIST_META[kind];
        // Inactive values are not offered and are not shown: the card is the
        // PICKER's contents, and a deactivated value is no longer in it.
        const values = optionsByKind[kind].filter((option) => option.is_active);

        return (
          <SettingsRow key={kind} roomy divided={index < LIST_KINDS.length - 1}>
            <View style={styles.head}>
              <View style={styles.headText}>
                <Text style={styles.label}>{meta.label}</Text>
                <Text style={styles.note}>{meta.note}</Text>
              </View>
              <Button label="Add" size="sm" variant="outline" onPress={() => onAdd(kind)} />
            </View>

            <View style={styles.chips}>
              {values.map((option) => (
                <RemovableChip
                  key={option.id}
                  label={option.display_name || option.value}
                  // Currency codes are mono; the rest are prose.
                  monospace={kind === 'currency'}
                  // The API refuses to remove the last value in a list, and so
                  // does the UI — there is no useful state with an empty picker.
                  disabled={values.length <= 1}
                  onRemove={() => onRemove(option)}
                />
              ))}
            </View>
          </SettingsRow>
        );
      })}
    </SettingsCard>
  );
}

const styles = StyleSheet.create({
  head: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 9,
  },
  headText: { flex: 1, minWidth: 0, gap: 2 },
  label: { ...sans('500'), fontSize: fontSize.button, color: neutral.ink },
  note: {
    ...sans('400'),
    fontSize: fontSize.meta,
    lineHeight: leading(fontSize.meta),
    color: neutral.grey600,
  },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 5 },
});

export default OptionListsCard;
